import { Button } from "@ths/ui";
import type { FC } from "react";
import { useMemo } from "react";

import EmailVerificationPanel from "../components/EmailVerificationPanel";
import { FacilityMap } from "../components/FacilityMap";
import type { GetSelfCheckinReservationQuery } from "../generated/graphql";

import { getStayDisplayDates, toDisplayDate } from "./dateFormatting";

type ReservationData = GetSelfCheckinReservationQuery["selfCheckinReservation"];

interface ReservationStepProps {
  reservation: ReservationData;
  onCheckOut: () => void;
  isCheckingOut: boolean;
  isStale?: boolean;
  isRefreshing?: boolean;
  onReportIssue?: () => void;
  error?: string;
}

const ReservationStep: FC<ReservationStepProps> = ({
  reservation: data,
  onCheckOut,
  isCheckingOut,
  isStale = false,
  isRefreshing = false,
  onReportIssue,
  error,
}) => {
  const canCheckOut = Boolean(data.checkedInAt) && !data.checkedOutAt;
  const stayEnded = useMemo(() => {
    if (data.checkedOutAt) return true;
    if (!data.checkOutDate) return false;
    const [y, m, d] = data.checkOutDate.split("-").map(Number);
    const endOfCheckOutDay = new Date(y, m - 1, d);
    endOfCheckOutDay.setHours(23, 59, 59, 999);
    return endOfCheckOutDay.getTime() < Date.now();
  }, [data.checkOutDate, data.checkedOutAt]);
  const formattedDates = useMemo(() => {
    const fmt = (d: string | null | undefined) =>
      d
        ? toDisplayDate(d).toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
          })
        : "Open";
    const dates = getStayDisplayDates(data);

    return {
      checkIn: fmt(dates.checkIn),
      checkOut: fmt(dates.checkOut),
    };
  }, [data]);
  const shouldShowWelcomeGuide =
    !isStale && !isRefreshing && Boolean(data.welcomePamphletUrl);

  return (
    <>
      <main
        className="relative z-10 flex flex-1 flex-col items-center px-6 pt-4 sm:pt-6"
        style={{
          paddingBottom: canCheckOut
            ? "calc(8rem + env(safe-area-inset-bottom))"
            : "2rem",
        }}
      >
        <div className="w-full max-w-sm space-y-4 sm:space-y-5">
          {isStale && (
            <div className="bc-fade-up rounded-xl border border-amber-500/20 bg-amber-500/[0.06] px-4 py-2.5 text-center">
              <p className="text-xs font-medium text-amber-300/70">
                Showing cached data — you may be offline
              </p>
            </div>
          )}

          {isRefreshing && !isStale && (
            <div className="bc-fade-up rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-2.5 text-center">
              <p className="inline-flex items-center gap-2 text-xs font-medium text-on-navy/75">
                <span className="size-1.5 animate-pulse rounded-full bg-white/40" />
                Refreshing…
              </p>
            </div>
          )}

          {stayEnded && (
            <div className="bc-fade-up rounded-2xl border border-amber-500/20 bg-amber-500/10 px-5 py-4 text-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-300/80">
                Your Stay Has Ended
              </p>
              <p className="mt-1.5 text-sm font-medium text-amber-100/90">
                Please check out to complete your session.
              </p>
            </div>
          )}

          {/* Room assignment — the hero */}
          <div className="bc-fade-up rounded-2xl border border-on-navy/15 bg-on-navy/5 px-6 py-4 text-center sm:py-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-on-navy/75">
              Your Room
            </p>
            <p className="mt-2 font-oswald text-5xl font-medium tracking-tight text-on-navy sm:text-7xl">
              {data.roomNumber}
            </p>
            {data.buildingName && (
              <p className="mt-2 text-sm font-medium text-on-navy/75">
                {data.buildingName}
              </p>
            )}
          </div>

          {/* Access codes — side by side */}
          {(data.buildingAccessCode || data.roomAccessCode) && (
            <div className="bc-fade-up bc-fade-up-1 space-y-2">
              <p className="text-center text-xs font-medium text-on-navy/75">
                Enter these codes at the keypad
              </p>
              <div
                className={`grid gap-3 ${data.buildingAccessCode && data.roomAccessCode ? "grid-cols-2" : "grid-cols-1"}`}
              >
                {data.buildingAccessCode && (
                  <div className="rounded-2xl border border-highlight/15 bg-highlight/[0.04] p-4 text-center">
                    <svg
                      className="mx-auto size-5 text-highlight/50"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"
                      />
                    </svg>
                    <p className="mt-2 text-xs font-bold uppercase tracking-[0.15em] text-on-navy/75">
                      Building Code
                    </p>
                    <p className="mt-1.5 font-mono text-2xl font-bold tracking-[0.15em] text-highlight">
                      {data.buildingAccessCode}
                    </p>
                  </div>
                )}
                {data.roomAccessCode && (
                  <div className="rounded-2xl border border-highlight/15 bg-highlight/[0.04] p-4 text-center">
                    <svg
                      className="mx-auto size-5 text-highlight/50"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z"
                      />
                    </svg>
                    <p className="mt-2 text-xs font-bold uppercase tracking-[0.15em] text-on-navy/75">
                      Door Code
                    </p>
                    <p className="mt-1.5 font-mono text-2xl font-bold tracking-[0.15em] text-highlight">
                      {data.roomAccessCode}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Facility codes — shared per-basecamp codes, server-filtered by gender */}
          {data.facilityCodes && data.facilityCodes.length > 0 && (
            <div className="bc-fade-up bc-fade-up-2 space-y-2">
              <p className="text-center text-xs font-medium text-on-navy/75">
                Facility Codes
              </p>
              <div
                className={`grid gap-3 ${
                  data.facilityCodes.length === 1
                    ? "grid-cols-1"
                    : "grid-cols-2"
                }`}
              >
                {data.facilityCodes.map((fc) => (
                  <div
                    key={fc.id}
                    className="rounded-2xl border border-highlight/15 bg-highlight/[0.04] p-4 text-center"
                  >
                    <p className="mt-2 text-xs font-bold uppercase tracking-[0.15em] text-on-navy/75">
                      {fc.label}
                    </p>
                    <p className="mt-1.5 font-mono text-2xl font-bold tracking-[0.15em] text-highlight">
                      {fc.code}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isStale && !isRefreshing && (
            <EmailVerificationPanel
              token={data.token}
              guestEmail={data.guestEmail ?? null}
              emailVerified={data.emailVerified ?? false}
            />
          )}

          {shouldShowWelcomeGuide && data.welcomePamphletUrl && (
            <div className="bc-fade-up bc-fade-up-3">
              <a
                href={data.welcomePamphletUrl}
                target="_blank"
                rel="noreferrer"
                aria-label="Open Basecamp Guide"
                className="flex items-center gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.03] p-4 text-left transition-colors hover:border-white/15 hover:bg-white/[0.06] active:scale-[0.99]"
              >
                <span className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-highlight/15 bg-highlight/[0.04] text-highlight">
                  <svg
                    className="size-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5A3.375 3.375 0 0010.125 2.25H8.25m0 12.75h7.5m-7.5 3h4.5m.75-15H6.375A1.875 1.875 0 004.5 5.625v12.75A1.875 1.875 0 006.375 20.25h11.25a1.875 1.875 0 001.875-1.875V11.25A9 9 0 0012.75 2.25z"
                    />
                  </svg>
                </span>
                <span className="min-w-0 flex-1 text-sm font-medium text-on-navy/75">
                  Basecamp Guide
                </span>
                <svg
                  className="size-4 shrink-0 text-on-navy/75"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 6H18m0 0v4.5M18 6l-7.5 7.5M6 18h12"
                  />
                </svg>
              </a>
            </div>
          )}

          {/* Dates */}
          <div className="bc-fade-up bc-fade-up-4 overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.03]">
            <div className="grid grid-cols-2 divide-x divide-white/[0.06]">
              <div className="p-4 text-center">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-on-navy/75">
                  Arrive
                </p>
                <p className="mt-1.5 font-oswald text-base font-light text-on-navy/75">
                  {formattedDates.checkIn}
                </p>
              </div>
              <div className="p-4 text-center">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-on-navy/75">
                  Depart
                </p>
                <p className="mt-1.5 font-oswald text-base font-light text-on-navy/75">
                  {formattedDates.checkOut}
                </p>
              </div>
            </div>
          </div>

          {/* Map */}
          {data.mapImageUrl && (
            <div className="bc-fade-up bc-fade-up-4 space-y-2">
              <p className="text-center text-xs font-medium text-on-navy/75">
                {data.buildingName
                  ? `Find ${data.buildingName} on the map below`
                  : "Find your building on the map below"}
              </p>
              <FacilityMap mapImageUrl={data.mapImageUrl} />
            </div>
          )}
        </div>
      </main>

      {/* Sticky check-out dock */}
      {canCheckOut && (
        <footer
          aria-label="Stay actions"
          className="fixed inset-x-0 bottom-0 z-20 border-t border-on-navy/15 bg-navy-strong/95 px-6 pt-3 backdrop-blur-md"
          style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
        >
          <div className="mx-auto flex w-full max-w-sm flex-col gap-2">
            {onReportIssue && (
              <Button
                type="button"
                size="lg"
                onClick={onReportIssue}
                className="w-full rounded-xl border border-on-navy/30 focus-visible:ring-on-navy focus-visible:ring-offset-navy-strong"
              >
                Report an issue
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="lg"
              onClick={onCheckOut}
              disabled={isCheckingOut}
              className="w-full rounded-xl border border-on-navy/30 bg-on-navy/10 text-on-navy hover:bg-on-navy/15 focus-visible:ring-on-navy focus-visible:ring-offset-navy-strong"
            >
              {isCheckingOut ? "Loading..." : "Check Out"}
            </Button>
          </div>
        </footer>
      )}

      {error && (
        <div className="bc-fade-up fixed inset-x-0 bottom-32 z-30 mx-auto max-w-sm px-6">
          <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-center text-sm font-medium text-red-300">
            {error}
          </div>
        </div>
      )}
    </>
  );
};

export default ReservationStep;
